import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  ChartType,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface CodeOutputChartProps {
  codeString: string;
}

type SupportedChartType = 'bar' | 'line' | 'pie' | 'doughnut';

const chartComponents = {
  'bar': Bar,
  'line': Line,
  'pie': Pie,
  'doughnut': Doughnut
} as const;

const CodeOutputChart: React.FC<CodeOutputChartProps> = ({ codeString }) => {
  const chartConfig = useMemo(() => {
    try {
      // Extract the JSON configuration
      const jsonStr = codeString.match(/\{[\s\S]*\}/)?.[0];
      if (!jsonStr) {
        throw new Error("Could not extract chart configuration");
      }

      // Parse the JSON configuration
      const config = JSON.parse(jsonStr);
      
      // Convert function strings back to functions
      if (config.options?.plugins?.tooltip?.callbacks?.label) {
        const labelFnStr = config.options.plugins.tooltip.callbacks.label;
        if (typeof labelFnStr === 'string' && labelFnStr.startsWith('function')) {
          // eslint-disable-next-line no-new-func
          config.options.plugins.tooltip.callbacks.label = new Function('context',
            `return ${labelFnStr.replace('function(context)', '')}`
          );
        }
      }

      return config;
    } catch (error) {
      console.error('Error parsing chart configuration:', error);
      return null;
    }
  }, [codeString]);

  if (!chartConfig) {
    return <div>Error parsing chart configuration</div>;
  }

  const ChartComponent = chartComponents[chartConfig.type as SupportedChartType] || chartComponents.bar;

  return (
    <div style={{ 
      width: '500px', 
      height: '500px',
      padding: '20px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <ChartComponent
        data={chartConfig.data}
        options={chartConfig.options}
      />
    </div>
  );
};

export default CodeOutputChart; 